import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { differenceInMinutes, formatDistanceToNow } from 'date-fns'
import { ONLINE_THRESHOLD_MINUTES, STALE_THRESHOLD_MINUTES } from '@/config/constants'
import type { DeviceStatus } from '@/types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatRelativeTime(date: string | null): string {
  if (!date) return 'Never';
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}

export function getTimeZoneAbbr(
  date: string | Date = new Date(),
  timeZone?: string | null
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const opts: Intl.DateTimeFormatOptions = { timeZoneName: 'short' };
  if (timeZone) opts.timeZone = timeZone;
  const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(d);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

export type DateTimePreset = 'datetime' | 'time' | 'datetime24' | 'datetime24sec';

const dateTimePresets: Record<DateTimePreset, Intl.DateTimeFormatOptions> = {
  // "May 4, 3:46 PM"
  datetime: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
  // "3:46 PM"
  time: { hour: 'numeric', minute: '2-digit', hour12: true },
  // "May 4, 19:15"
  datetime24: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
  // "May 4, 19:15:23"
  datetime24sec: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
};

// Formats a date in the given IANA timezone, falling back to the browser's
// local zone when timeZone is null/undefined.
export function formatLocalDateTime(
  date: string | Date,
  preset: DateTimePreset,
  timeZone?: string | null
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const opts: Intl.DateTimeFormatOptions = { ...dateTimePresets[preset] };
  if (timeZone) opts.timeZone = timeZone;
  return new Intl.DateTimeFormat('en-US', opts).format(d);
}

export function getDeviceStatus(lastCheckinAt: string | null): DeviceStatus {
  if (!lastCheckinAt) return 'offline';
  const minutesAgo = differenceInMinutes(new Date(), new Date(lastCheckinAt));
  if (minutesAgo <= ONLINE_THRESHOLD_MINUTES) return 'online';
  if (minutesAgo <= STALE_THRESHOLD_MINUTES) return 'stale';
  return 'offline';
}

export function formatCoordinate(value: number): string {
  return value.toFixed(6);
}

export function batteryPercent(level: number | null | undefined): number | null {
  if (level == null) return null;
  return Math.round(level * 100);
}

export function formatBatteryPercent(level: number | null | undefined): string {
  const pct = batteryPercent(level);
  return pct == null ? '—' : `${pct}%`;
}

// Formats US E.164 phone numbers (e.g. "+15551234567") as "(555) 123-4567".
// Falls back to the raw input for non-US or malformed values.
export function formatPhoneNumber(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}
